import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  destinationForDeepLink,
  isSafeDeepLinkPath,
  savePendingDeepLink,
} from '../lib/deepLinks';

function pathFromMessage(data: {
  path?: unknown;
  url?: unknown;
}): string | null {
  if (typeof data.path === 'string' && data.path.startsWith('/')) {
    return data.path;
  }
  if (typeof data.url === 'string' && data.url.trim()) {
    try {
      const u = new URL(data.url, window.location.origin);
      if (u.origin !== window.location.origin) return null;
      return `${u.pathname}${u.search}${u.hash}`;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Service worker notificationclick → in-app navigation.
 * Uses /c/:id and /t/:id so unsigned sessions still recover via pending deep link.
 */
const NotificationNavRunner: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== 'NOTIFICATION_NAV') return;

      const path = pathFromMessage(data);
      if (!path) return;

      const chatMatch = path.match(/^\/c\/([^/?#]+)\/?$/);
      if (chatMatch) {
        const dest = destinationForDeepLink('chat', chatMatch[1]);
        if (dest) {
          savePendingDeepLink(dest);
          history.push(`/c/${chatMatch[1]}`);
          return;
        }
      }

      const topicMatch = path.match(/^\/t\/([^/?#]+)\/?$/);
      if (topicMatch) {
        const dest = destinationForDeepLink('topic', topicMatch[1]);
        if (dest) {
          savePendingDeepLink(dest);
          history.push(`/t/${topicMatch[1]}`);
          return;
        }
      }

      if (isSafeDeepLinkPath(path) || path.startsWith('/dashboard') || path.startsWith('/chat/')) {
        history.push(path);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, [history]);

  return null;
};

export default NotificationNavRunner;
